import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileText, 
  faUser, 
  faCalendar,
  faTag
} from '@fortawesome/free-solid-svg-icons';

interface ScriptData {
  id: string;
  title?: string;
  generatedScript: string;
  userSelections: {
    userIdea: string;
    attributes?: string;
  };
  scriptMetadata?: {
    scriptType?: string;
  };
  createdAt: string;
}

interface ScriptPreviewProps {
  script: ScriptData;
}

const ScriptPreview: React.FC<ScriptPreviewProps> = ({ script }) => {
  const { t } = useTranslation('scriptGenerator');
  
  const parseAttributes = (script: ScriptData) => {
    try {
      if (script.userSelections?.attributes && 
          typeof script.userSelections.attributes === 'string') {
        return JSON.parse(script.userSelections.attributes);
      }
      return {};
    } catch (error) {
      console.error('Error parsing script attributes:', error);
      return {};
    }
  };

  const attributes = parseAttributes(script);
  const scriptPreview = script.generatedScript.substring(0, 200) + 
    (script.generatedScript.length > 200 ? '...' : '');

  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      padding: '16px',
      marginTop: '12px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      }}>
        <FontAwesomeIcon icon={faFileText} style={{ color: '#3b82f6' }} />
        <h4 style={{
          margin: 0,
          color: '#e0e0e0',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          {t('scriptGenerator.scriptPreview.title')}
        </h4>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '8px',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FontAwesomeIcon icon={faUser} style={{ color: '#888', fontSize: '12px' }} />
          <span style={{ color: '#a0a0a0', fontSize: '12px' }}>
            {script.userSelections?.userIdea?.substring(0, 30) + 
             (script.userSelections?.userIdea?.length > 30 ? '...' : '')}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FontAwesomeIcon icon={faCalendar} style={{ color: '#888', fontSize: '12px' }} />
          <span style={{ color: '#a0a0a0', fontSize: '12px' }}>
            {new Date(script.createdAt).toLocaleDateString()}
          </span>
        </div>

        {(script.scriptMetadata?.scriptType || attributes.genre) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FontAwesomeIcon icon={faTag} style={{ color: '#888', fontSize: '12px' }} />
            <span style={{ color: '#a0a0a0', fontSize: '12px' }}>
              {script.scriptMetadata?.scriptType || attributes.genre}
            </span>
          </div>
        )}
      </div>

      <div style={{
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
        padding: '12px',
        fontFamily: 'monospace',
        fontSize: '13px',
        lineHeight: '1.4',
        color: '#d0d0d0',
        border: '1px solid rgba(255, 255, 255, 0.05)'
      }}>
        {scriptPreview}
      </div>

      <div style={{
        marginTop: '8px',
        fontSize: '12px',
        color: '#888',
        textAlign: 'right'
      }}>
        {script.generatedScript.length} {t('scriptGenerator.scriptPreview.charactersTotal')}
      </div>
    </div>
  );
};

export default ScriptPreview;